import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';

// Rotating film-set words for the kicker line
const ROTATING_WORDS = ['cut', 'take', 'frame', 'scene', 'reel', 'shot'];

const Page: PageType = () => {
  usePageView();

  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setWordVisible(true);
      }, 280);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Seo
        title="Page Not Found | Mystic Media Film"
        description="The page you’re looking for didn’t make the cut. Head back to the Mystic Media Film home reel or meet the team."
      />
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        sx={{
          minHeight: 'calc(100vh - 96px)',
          position: 'relative',
          background: '#ffffff',
          color: '#0a0a0b',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 12, md: 16 },
          pb: { xs: 12, md: 16 }
        }}
      >
        {/* Subtle blue glow behind the 404 */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -55%)',
            width: { xs: 520, md: 880 },
            height: { xs: 520, md: 880 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(37, 99, 235, 0.10) 0%, transparent 60%)',
            filter: 'blur(60px)',
            pointerEvents: 'none'
          }}
        />
        {/* Indigo accent blob, top-right */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-8%',
            width: 460,
            height: 460,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 65%)',
            filter: 'blur(80px)',
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack alignItems="center" spacing={{ xs: 4, md: 5 }} sx={{ textAlign: 'center' }}>
            {/* Eyebrow pill (matches home/about hero pill style) */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                ✶
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: '#0a0a0b'
                }}
              >
                Error code: 404
              </Typography>
            </Stack>

            {/* Giant 404 with subtle gradient and blue glow */}
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 120, sm: 200, md: 260 },
                fontWeight: 800,
                lineHeight: 0.9,
                letterSpacing: '-0.06em',
                background:
                  'linear-gradient(180deg, #0a0a0b 0%, #1e293b 55%, rgba(37, 99, 235, 0.85) 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                userSelect: 'none',
                mt: { xs: -1, md: -2 }
              }}
            >
              404
            </Typography>

            {/* Headline with rotating word (matches home/about hero pattern) */}
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 28, md: 44 },
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '-0.03em',
                color: '#0a0a0b',
                px: { xs: 2, md: 0 }
              }}
            >
              This{' '}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  fontStyle: 'italic',
                  color: '#0a0a0b',
                  opacity: wordVisible ? 1 : 0,
                  transform: wordVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.28s ease, transform 0.28s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '0.08em',
                    height: '0.32em',
                    background: 'rgba(37, 99, 235, 0.18)',
                    zIndex: -1,
                    borderRadius: 2
                  }
                }}
              >
                {ROTATING_WORDS[wordIndex]}
              </Box>
              {' '}didn’t make the final.
            </Typography>

            {/* Sub */}
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 16, md: 18 },
                fontWeight: 400,
                lineHeight: 1.6,
                color: '#52525b',
                maxWidth: 540,
                px: { xs: 2, md: 0 }
              }}
            >
              The link you followed is broken, or the page may have moved off camera. Use the nav above, or jump back to the home reel.
            </Typography>

            {/* CTAs */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ pt: 1 }}
            >
              <Button
                component={RouterLink}
                href={paths.index}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: '#2563eb',
                  color: '#ffffff',
                  textTransform: 'none',
                  boxShadow: '0 8px 24px -8px rgba(37, 99, 235, 0.45)',
                  '&:hover': {
                    background: '#1d4ed8',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 12px 28px -8px rgba(37, 99, 235, 0.55)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Back to home
              </Button>
              <Button
                component={RouterLink}
                href={paths.about.index}
                variant="outlined"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  borderColor: '#e4e4e7',
                  borderWidth: 1.5,
                  color: '#0a0a0b',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    backgroundColor: 'rgba(37, 99, 235, 0.04)',
                    borderWidth: 1.5
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Meet the team
              </Button>
            </Stack>

            {/* Bottom film-set frame counter (mono, muted) */}
            <Stack
              direction="row"
              spacing={{ xs: 2, md: 4 }}
              sx={{
                pt: { xs: 5, md: 6 },
                fontFamily: '"Roboto Mono", monospace'
              }}
            >
              {['SCENE 404', 'TAKE 01', 'ROLL DROPPED'].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: { xs: 10, md: 11 },
                    fontWeight: 500,
                    letterSpacing: 1.8,
                    textTransform: 'uppercase',
                    color: '#a1a1aa'
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
